import React, { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';
import { getMedicalSuppliesForm } from '../../utils/medicalSuppliesApi';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;  
  font-family: sans-serif;
`;

const BlueHeader = styled.div`
  background: #1e3a8a;
  color: white;
  padding: 20px;
  margin: -40px -32px 30px -32px;
  border-radius: 15px 15px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const LogoLetter = styled.div`
  width: 60px;
  height: 60px;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  
  &::after {
    content: attr(data-text);
    position: absolute;
    bottom: -15px;
    font-size: 8px;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const FormTitle = styled.h1`
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  text-transform: uppercase;
  margin: 30px 0;
  color: #2c3e50;
  font-family: sans-serif;
`;

const FormContent = styled.div`
  font-family: sans-serif;
  line-height: 1.6;
  color: #333;
`;

const AcknowledgmentStatement = styled.div`
  margin: 20px 0;
  font-size: 1rem;
  line-height: 1.8;
`;

const Blank = styled.span`
  display: inline-block;
  min-width: 200px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const ShortBlank = styled.span`
  display: inline-block;
  min-width: 80px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin: 25px 0 15px 0;
  color: #2c3e50;
  font-style: italic;
`;



const CourseSection = styled.div`
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f8f9fa;
`;

const CourseLabel = styled.label`
  font-weight: 600;
  color: #2c3e50;
  margin-right: 15px;
`;

const CourseSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 1rem;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 650px;
`;

const SignatureSection = styled.div`
  margin: 40px 0;
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0 0;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  color: #222;
`;

const SigValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 300px;
  display: inline-block;
`;

const SignatureImage = styled.img`
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border-bottom: 1px solid #333;
  margin-bottom: 16px;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  mix-blend-mode: multiply;
  filter: contrast(1.2);
`;

const DateValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 150px;
  display: inline-block;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  font-size: 0.8rem;
  color: #666;
`;

const FooterLogo = styled.div`
  width: 20px;
  height: 20px;
  background: #333;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const NoFormMessage = styled.div`
  padding: 60px 24px;
  border-radius: 12px;
  text-align: center;
  font-weight: 500;
  margin: 20px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NoFormTitle = styled.h2`
  color: #1565c0;
  margin-bottom: 16px;
  font-size: 1.5rem;
`;

const NoFormText = styled.p`
  margin: 12px 0;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ActionButton = styled.button`
  background: #1976d2;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;

  &:hover {
    background: #1565c0;
    transform: translateY(-1px);
  }
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const MedicalSuppliesAcknowledgmentForm = () => {
  const { id } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();
  const [medicalSuppliesData, setMedicalSuppliesData] = useState(null);
  const [medicalSuppliesLoading, setMedicalSuppliesLoading] = useState(true);
  const [medicalSuppliesError, setMedicalSuppliesError] = useState(null);

  const handleDownload = () => {
    window.print();
  };

  // Fetch medical supplies form data
  useEffect(() => {
    const fetchMedicalSuppliesData = async () => {
      if (!id) return;
      
      try {
        setMedicalSuppliesLoading(true);
        const result = await getMedicalSuppliesForm(id);
        if (result.success) {
          setMedicalSuppliesData(result.data);
        } else {
          setMedicalSuppliesError('Failed to fetch medical supplies data');
        }
      } catch (error) {
        console.error('Error fetching medical supplies data:', error);
        setMedicalSuppliesError('Failed to fetch medical supplies data');
      } finally {
        setMedicalSuppliesLoading(false);
      }
    };

    fetchMedicalSuppliesData();
  }, [id]);

  if (loading || medicalSuppliesLoading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }
  if (medicalSuppliesError) {
    // Check if the error is due to no form data (404 or similar)
    if (medicalSuppliesError.includes('Failed to fetch medical supplies data')) {
      return (
        <Container>
          <NoFormMessage style={{ marginTop: '200px' }}>
            <NoFormTitle style={{ marginBottom: '50px' }}>The student has not submitted the Medical Supplies Acknowledgment Form</NoFormTitle>
            <NoFormText>
              It looks the student has not filled out the Medical Supplies Acknowledgment Form yet.
            </NoFormText>
            <NoFormText >
             Ask the student to submit the form.
            </NoFormText>
           
          </NoFormMessage>
        </Container>
      );
    }
    // For other types of errors, show the original error message
    return <Container><ErrorMessage>{medicalSuppliesError}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container style={{ padding: '40px' }}>
      <PrintHideStyle />
      <div ref={docRef} style={{ background: '#fff', color: '#222', padding: 0 }}>
        
        <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />
        
        <FormTitle>MEDICAL SUPPLIES ACKNOWLEDGMENT FORM</FormTitle>
        
        <FormContent>
          <AcknowledgmentStatement>
            I acknowledge that I have received the following supplies for the course: <Blank>{medicalSuppliesData?.course || student?.course || 'N/A'}</Blank>
          </AcknowledgmentStatement>
          <SectionTitle>List of supplies:</SectionTitle>
          
          <div style={{ marginLeft: '20px', marginTop: '15px' }}>
            {(() => {
              // Get supplies from medical supplies form data
              const suppliesReceived = medicalSuppliesData?.supplies_received;
              
              if (suppliesReceived && suppliesReceived !== 'N/A' && suppliesReceived.trim() !== '') {
                // Split the comma-separated string and create bullet points
                const suppliesList = suppliesReceived.split(',').map(item => item.trim());
                
                return (
                  <ul style={{ 
                    listStyleType: 'disc', 
                    margin: 0, 
                    paddingLeft: '20px',
                    fontSize: '1rem',
                    lineHeight: '1.8'
                  }}>
                    {suppliesList.map((supply, index) => (
                      <li key={index} style={{ marginBottom: '8px' }}>
                        {supply}
                      </li>
                    ))}
                  </ul>
                );
              } else {
                return (
                  <div style={{ 
                    fontStyle: 'italic', 
                    color: '#6c757d',
                    marginLeft: '20px'
                  }}>
                    No supplies data available
                  </div>
                );
              }
            })()}
          </div>

          <AcknowledgmentStatement>
            I understand that these supplies are provided as part of my course materials and are essential for my training. I will take proper care of these items and return them in good condition if required by the institution.
          </AcknowledgmentStatement>

          <SignatureSection>
            <SignatureRow>
              <SigLabel>Student Signature:</SigLabel>
              {(() => {
                // First try to get signature from medical supplies form data
                const medSupSigData = medicalSuppliesData?.medsup_signature_data;
                
                if (medSupSigData) {
                  // Check if it's a valid data URL (must have content after data:)
                  if (typeof medSupSigData === 'string' && medSupSigData.startsWith('data:') && medSupSigData.length > 6) {
                    return (
                      <SignatureImage src={medSupSigData} alt="Student Signature" />
                    );
                  }
                  
                  // Check if it's SVG markup
                  if (typeof medSupSigData === 'string' && medSupSigData.trim().startsWith('<svg')) {
                    return (
                      <div 
                        dangerouslySetInnerHTML={{ __html: medSupSigData }}
                        style={{ maxHeight: 40, maxWidth: 200, backgroundColor: 'transparent' }}
                      />
                    );
                  }
                  
                  // Check if it's a base64 string (without data URL prefix)
                  if (typeof medSupSigData === 'string' && medSupSigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(medSupSigData)) {
                    return (
                      <SignatureImage 
                        src={`data:image/png;base64,${medSupSigData}`} 
                        alt="Student Signature" 
                      />
                    );
                  }
                  
                  // Check if it's a file path
                  if (typeof medSupSigData === 'string' && (medSupSigData.includes('.png') || medSupSigData.includes('.jpg') || medSupSigData.includes('.jpeg'))) {
                    return (
                      <SignatureImage src={medSupSigData} alt="Student Signature" />
                    );
                  }
                }
                
                // Fallback to student acknowledgment signature if medical supplies signature not available
                const sigData = student.student_ack_signature_data;
                
                if (sigData) {
                  // Check if it's a valid data URL (must have content after data:)
                  if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                    return (
                      <SignatureImage src={sigData} alt="Student Signature" />
                    );
                  }
                  
                  // Check if it's SVG markup
                  if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                    return (
                      <div 
                        dangerouslySetInnerHTML={{ __html: sigData }}
                        style={{ maxHeight: 40, maxWidth: 200, backgroundColor: 'transparent' }}
                      />
                    );
                  }
                  
                  // Check if it's a base64 string (without data URL prefix)
                  if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                    return (
                      <SignatureImage 
                        src={`data:image/png;base64,${sigData}`} 
                        alt="Student Signature" 
                      />
                    );
                  }
                  
                  // Check if it's a file path
                  if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                    return (
                      <SignatureImage src={sigData} alt="Student Signature" 
                      
                      />
                    );
                  }
                }
                
                // If no signature data available, show empty signature field with underline
                return <SigValue></SigValue>;
              })()}
              <SigLabel style={{ marginLeft: '20px' }}>Date:</SigLabel>
              <DateValue>{medicalSuppliesData?.date || dateStr}</DateValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Full Name (please print):</SigLabel>
              <SigValue>{student.firstName} {student.lastName}</SigValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Email Address:</SigLabel>
              <SigValue>{student.email || 'N/A'}</SigValue>
            </SignatureRow>
            
            <SignatureRow>
              <SigLabel>Address:</SigLabel>
              <SigValue>
                {student.address || student.enroll_address || 'N/A'}
                {student.city || student.enroll_city ? `, ${student.city || student.enroll_city}` : ''}
                {student.state || student.enroll_state ? `, ${student.state || student.enroll_state}` : ''}
                {student.zipCode || student.enroll_zip ? ` ${student.zipCode || student.enroll_zip}` : ''}
                {student.country || student.enroll_country ? `, ${student.country || student.enroll_country}` : ''}
              </SigValue>
            </SignatureRow>
          </SignatureSection>

        </FormContent>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default MedicalSuppliesAcknowledgmentForm;
